import { MARKETS, type Market } from '@ambernord/shared';
import {
  Button,
  Card,
  CardBody,
  Field,
  Input,
  Select,
  type StepDef,
  Stepper,
  Textarea,
  cn,
  useToast,
} from '@ambernord/ui';
import { CircleCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { productName } from '../lib/format.js';

interface ProductItem {
  id: string;
  sku: string;
  category?: string;
  unit?: string;
  namesI18n: Record<string, string>;
}

const KNOWN_ERROR_CODES = ['customer_profile_not_set_up', 'unknown_product', 'invalid_input'];

type Step = 'details' | 'review';

export default function NewOrder() {
  const { t, i18n } = useTranslation('neworder');
  const lang = i18n.language;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('1');
  const [market, setMarket] = useState<Market>('eu');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<Step>('details');
  const [placedOrderNo, setPlacedOrderNo] = useState<string | null>(null);
  const [placed, setPlaced] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: ProductItem[] }>('/api/products');
      if (!active || !res.ok) return;
      const items = res.data.items ?? [];
      setProducts(items);
      if (items[0]) setProductId(items[0].id);
    })();
    return () => {
      active = false;
    };
  }, []);

  const selected = products.find((p) => p.id === productId);

  function goToReview() {
    setError(null);
    const qtyNum = Number(qty);
    if (!productId) {
      setError(t('errors.selectProduct'));
      return;
    }
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setError(t('errors.quantityPositive'));
      return;
    }
    setStep('review');
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string; order?: { orderNo?: string } }>('/api/orders', {
        market,
        lines: [{ productId, qty: Number(qty) }],
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      if (!res.ok) {
        const code = res.data.error ?? '';
        setError(KNOWN_ERROR_CODES.includes(code) ? t(`errors.${code}`) : t('errors.generic'));
        return;
      }
      setPlacedOrderNo(res.data.order?.orderNo ?? null);
      setPlaced(true);
      toast(t('success.toast'));
    } catch {
      setError(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setPlaced(false);
    setPlacedOrderNo(null);
    setStep('details');
    setQty('1');
    setNotes('');
    setError(null);
    if (products[0]) setProductId(products[0].id);
  }

  const steps: StepDef[] = [
    {
      key: 'details',
      label: t('steps.details'),
      state: placed || step === 'review' ? 'done' : 'current',
    },
    {
      key: 'review',
      label: t('steps.review'),
      state: placed ? 'done' : step === 'review' ? 'current' : 'upcoming',
    },
  ];

  const categoryLabel = (p: ProductItem) =>
    p.category ? t(`category.${p.category}`, { defaultValue: p.category }) : null;

  // Veiksmes panelis — pēc iesniegšanas.
  if (placed) {
    return (
      <AppLayout title={t('title')}>
        <Card className="mx-auto max-w-lg">
          <CardBody className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
              <CircleCheck className="h-8 w-8" aria-hidden />
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {t('success.title')}
            </h2>
            {placedOrderNo && (
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('success.orderNo', { no: placedOrderNo })}
              </p>
            )}
            <p className="max-w-sm text-sm text-slate-500 dark:text-slate-400">
              {t('success.hint')}
            </p>
            <div className="mt-3 flex gap-2">
              <Button onClick={() => navigate('/orders')}>{t('success.viewOrders')}</Button>
              <Button variant="secondary" onClick={reset}>
                {t('success.newOrder')}
              </Button>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={t('title')}>
      <div className="mx-auto max-w-2xl">
        <Stepper steps={steps} className="mb-6" />

        {step === 'details' && (
          <div className="flex flex-col gap-5">
            <fieldset>
              <legend className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('form.product')}
              </legend>
              {products.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  {t('options.noProducts')}
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {products.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={productId === p.id}
                      onClick={() => setProductId(p.id)}
                      className={cn(
                        'flex flex-col gap-0.5 rounded-xl border p-3 text-left transition-colors',
                        productId === p.id
                          ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600 dark:border-brand-500 dark:bg-brand-900/30 dark:ring-brand-500'
                          : 'border-slate-200 bg-white hover:border-brand-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-brand-700',
                      )}
                    >
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        {productName(p.namesI18n, lang, p.sku)}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {[categoryLabel(p), p.unit ? t('form.perUnit', { unit: p.unit }) : null]
                          .filter(Boolean)
                          .join(' · ') || p.sku}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </fieldset>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('form.quantity')} htmlFor="qty">
                <Input
                  id="qty"
                  type="number"
                  min={1}
                  step="any"
                  required
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              </Field>
              <Field label={t('form.market')} htmlFor="market">
                <Select
                  id="market"
                  value={market}
                  onChange={(e) => setMarket(e.target.value as Market)}
                >
                  {MARKETS.map((m) => (
                    <option key={m} value={m}>
                      {m.toUpperCase()}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label={t('form.notes')} htmlFor="notes">
              <Textarea
                id="notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <div>
              <Button onClick={goToReview} disabled={products.length === 0}>
                {t('actions.continue')}
              </Button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <Card>
            <CardBody className="pt-4">
              <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">
                {t('review.heading')}
              </h2>
              <dl className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                {(
                  [
                    [t('form.product'), selected ? productName(selected.namesI18n, lang) : '—'],
                    [t('review.sku'), selected?.sku ?? '—'],
                    [t('review.category'), (selected && categoryLabel(selected)) ?? '—'],
                    [t('form.quantity'), `${qty} ${selected?.unit ?? ''}`.trim()],
                    [t('form.market'), market.toUpperCase()],
                    [t('form.notes'), notes.trim() || '—'],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 py-2">
                    <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                    <dd className="text-right font-medium text-slate-900 dark:text-white">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>

              {error && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
                  {error}
                </p>
              )}

              <div className="mt-5 flex gap-2">
                <Button variant="secondary" onClick={() => setStep('details')}>
                  {t('actions.back')}
                </Button>
                <Button disabled={submitting} onClick={() => void submit()}>
                  {submitting ? t('submitting') : t('actions.placeOrder')}
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
